import { Vec3 } from '../core/math';

const DEG = Math.PI / 180;

/**
 * Time of day and approximate solar / lunar positions for a Caribbean
 * latitude in early summer. Directions point toward the body.
 */
export class DayNight {
  /** Hours since midnight, [0, 24). */
  hours: number;
  /** Game seconds per real second. */
  timeScale = 20;
  latitude = 17 * DEG;
  dayOfYear = 172;
  /** Moon hour-angle lag behind "opposite the sun" (sets the phase). */
  moonLag = 0.55;

  readonly sunDir = new Vec3(0, 1, 0);
  readonly moonDir = new Vec3(0, -1, 0);
  sunElevation = 0;
  moonElevation = 0;

  constructor(hours = 16) {
    this.hours = hours;
    this.compute();
  }

  update(dt: number): void {
    this.hours = (this.hours + (dt * this.timeScale) / 3600) % 24;
    if (this.hours < 0) this.hours += 24;
    this.compute();
  }

  setHours(h: number): void {
    this.hours = ((h % 24) + 24) % 24;
    this.compute();
  }

  /** 0 at night, 1 in full day, smooth through twilight. */
  get daylight(): number {
    const e = this.sunElevation / DEG;
    return Math.min(1, Math.max(0, (e + 6) / 16));
  }

  private compute(): void {
    const decl = 23.44 * DEG * Math.sin((2 * Math.PI * (284 + this.dayOfYear)) / 365);
    const h = (this.hours - 12) * 15 * DEG;
    this.sunElevation = solar(this.latitude, decl, h, this.sunDir);
    const moonDecl = -decl * 0.7 + 4 * DEG;
    this.moonElevation = solar(this.latitude, moonDecl, h + Math.PI + this.moonLag, this.moonDir);
  }
}

/** Writes the direction toward a body at hour angle `h`; returns elevation. */
function solar(lat: number, decl: number, h: number, out: Vec3): number {
  const sinEl = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(h);
  const el = Math.asin(Math.max(-1, Math.min(1, sinEl)));
  const cosEl = Math.max(1e-6, Math.cos(el));
  let cosAz = (Math.sin(decl) - sinEl * Math.sin(lat)) / (cosEl * Math.cos(lat));
  cosAz = Math.max(-1, Math.min(1, cosAz));
  let az = Math.acos(cosAz);
  if (Math.sin(h) > 0) az = 2 * Math.PI - az;
  out.set(Math.sin(az) * cosEl, sinEl, -Math.cos(az) * cosEl);
  return el;
}
